/**
 * Hospital Starter Templates & Compliance Starter Definitions.
 * R39 C5.a1 & C5.a5 (plan docs/plans/sector-operations.md §2 SO-P252, H§5).
 * Starter templates labelled "starter, not certified", check with advisor.
 */

import type { OpsTemplateField } from './registers.js'
import type { OpsComplianceCategory } from './compliance.js'

export const HOS_STARTER_SOURCE = 'timelining:hospital:starter'
export const HOS_STARTER_EDITION = '2026.1'
export const HOS_STARTER_JURISDICTION = 'IN'

export function buildHosTemplateId(workspaceId: string, slug: string): string {
  const sanitized = slug.toLowerCase().replace(/[^a-z0-9_]/g, '_')
  return `tpl_hos_${workspaceId}_${sanitized}`
}

export interface HosStarterTemplateDef {
  starterKey: string
  slug: string
  name: string
  kind: 'CHECK' | 'LOG'
  safetyTagged: boolean
  patientLinked: false
  lateAfterMinutes: number
  source: string
  edition: string
  jurisdiction: string
  starter: true
  fields: OpsTemplateField[]
}

// ---------------------------------------------------------------------------
// 1. FIRE_DRILL
// ---------------------------------------------------------------------------
export const HOS_FIRE_DRILL_FIELDS: OpsTemplateField[] = [
  {
    key: 'drill_date_time',
    label: 'NLSOpsHosDrillDateTime',
    type: 'DATE_TIME',
    required: true,
  },
  {
    key: 'zone',
    label: 'NLSOpsHosDrillZone',
    type: 'CHOICE',
    options: ['ZONE_A', 'ZONE_B', 'ZONE_C', 'OPD', 'IPD', 'ICU', 'EMERGENCY', 'BASEMENT', 'OTHER'],
    required: true,
  },
  {
    key: 'participants_count',
    label: 'NLSOpsHosDrillParticipantsCount',
    type: 'NUMBER',
    min: 1,
    required: true,
  },
  {
    key: 'evacuation_time_minutes',
    label: 'NLSOpsHosDrillEvacuationMinutes',
    type: 'NUMBER',
    min: 0,
    required: true,
  },
  {
    key: 'issues_found',
    label: 'NLSOpsHosDrillIssuesFound',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'photo',
    label: 'NLSOpsHosDrillPhoto',
    type: 'PHOTO',
    required: false,
  },
]

// ---------------------------------------------------------------------------
// 2. FIRE_EQUIPMENT_MONTHLY
// ---------------------------------------------------------------------------
export const HOS_FIRE_EQUIPMENT_MONTHLY_FIELDS: OpsTemplateField[] = [
  {
    key: 'extinguisher_check',
    label: 'NLSOpsHosFireExtinguisherCheck',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'hydrant_hose_reel',
    label: 'NLSOpsHosFireHydrantHoseReel',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'alarm_panel',
    label: 'NLSOpsHosFireAlarmPanel',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'exits_clear',
    label: 'NLSOpsHosFireExitsClear',
    type: 'YES_NO',
    required: true,
  },
]

// ---------------------------------------------------------------------------
// 3. ELECTRICAL_INSPECTION
// ---------------------------------------------------------------------------
export const HOS_ELECTRICAL_INSPECTION_FIELDS: OpsTemplateField[] = [
  {
    key: 'db_panels',
    label: 'NLSOpsHosElecDbPanels',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'earthing_check',
    label: 'NLSOpsHosElecEarthingCheck',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'ups_dg_run_test',
    label: 'NLSOpsHosElecUpsDgRunTest',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'thermal_hotspot',
    label: 'NLSOpsHosElecThermalHotspot',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'photo',
    label: 'NLSOpsHosElecPhoto',
    type: 'PHOTO',
    required: false,
  },
]

// ---------------------------------------------------------------------------
// 4. KITCHEN_LOG
// ---------------------------------------------------------------------------
export const HOS_KITCHEN_LOG_FIELDS: OpsTemplateField[] = [
  {
    key: 'opening_hygiene',
    label: 'NLSOpsHosKitchenOpeningHygiene',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'fridge_temperature',
    label: 'NLSOpsHosKitchenFridgeTemperature',
    type: 'NUMBER',
    unit: '°C',
    required: true,
  },
  {
    key: 'food_handler_fitness',
    label: 'NLSOpsHosKitchenFoodHandlerFitness',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'pest_sign',
    label: 'NLSOpsHosKitchenPestSign',
    type: 'YES_NO',
    required: true,
  },
]

// ---------------------------------------------------------------------------
// 5. LAUNDRY_LOG
// ---------------------------------------------------------------------------
export const HOS_LAUNDRY_LOG_FIELDS: OpsTemplateField[] = [
  {
    key: 'soiled_out_count',
    label: 'NLSOpsHosLaundrySoiledOutCount',
    type: 'NUMBER',
    min: 0,
    required: true,
  },
  {
    key: 'clean_in_count',
    label: 'NLSOpsHosLaundryCleanInCount',
    type: 'NUMBER',
    min: 0,
    required: true,
  },
  {
    key: 'infected_linen_bag_sealed',
    label: 'NLSOpsHosLaundryInfectedBagSealed',
    type: 'YES_NO',
    required: true,
  },
]

// ---------------------------------------------------------------------------
// 6. CRASH_CART_CHECK (Parity with hospital-20-50-beds kit)
// ---------------------------------------------------------------------------
export const HOS_CRASH_CART_CHECK_FIELDS: OpsTemplateField[] = [
  {
    key: 'seal_intact_and_number_noted',
    label: 'Seal intact and number noted',
    type: 'PHOTO',
    required: true,
  },
  {
    key: 'cart_clean_and_in_place',
    label: 'Cart clean and in its place',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'self_test_passed',
    label: 'Self-test passed',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'battery_charged_pads_in_date',
    label: 'Battery charged, pads in date',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'items_present_approved_list',
    label: 'Items present as per your approved list',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'nearest_expiry_date',
    label: 'Nearest expiry date',
    type: 'DATE_TIME',
    required: true,
  },
  {
    key: 'oxygen_cylinder_pressure',
    label: 'Oxygen cylinder pressure',
    type: 'NUMBER',
    unit: 'psi',
    required: true,
  },
  {
    key: 'suction_working',
    label: 'Suction working',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'resealed_after_use_new_number',
    label: 'Re-sealed after use, new seal number',
    type: 'ACKNOWLEDGE',
    required: false,
  },
]

// ---------------------------------------------------------------------------
// 7. OXYGEN_SUCTION_READINESS
// ---------------------------------------------------------------------------
export const HOS_OXYGEN_SUCTION_READINESS_FIELDS: OpsTemplateField[] = [
  {
    key: 'oxygen_cylinder_levels',
    label: 'Oxygen cylinder levels & manifold pressure',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'suction_apparatus_pressure',
    label: 'Suction apparatus operational and vacuum adequate',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'backup_cylinders_keyed',
    label: 'Backup oxygen cylinders full and keyed',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'tubing_masks_stocked',
    label: 'Tubing, masks, and cannulas stocked',
    type: 'YES_NO',
    required: true,
  },
]

// ---------------------------------------------------------------------------
// 8. ICU_EQUIPMENT_READINESS
// ---------------------------------------------------------------------------
export const HOS_ICU_EQUIPMENT_READINESS_FIELDS: OpsTemplateField[] = [
  {
    key: 'ventilator_self_test',
    label: 'Ventilator self-test and circuit integrity',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'multipara_monitors_calibrated',
    label: 'Multiparameter monitors calibrated and leads intact',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'infusion_syringe_pumps',
    label: 'Infusion & syringe pumps functional on battery test',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'emergency_suction_defib',
    label: 'Emergency suction & portable defib ready',
    type: 'YES_NO',
    required: true,
  },
]

// ---------------------------------------------------------------------------
// 9. OT_ROOM_READINESS
// ---------------------------------------------------------------------------
export const HOS_OT_ROOM_READINESS_FIELDS: OpsTemplateField[] = [
  {
    key: 'anesthesia_machine_checkout',
    label: 'Anesthesia workstation checkout completed',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'scavenging_system_vacuum',
    label: 'Gas scavenging system & vacuum operational',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'ot_lights_table_ups',
    label: 'OT lights & motorized table functioning on UPS backup',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'hvac_hepa_positive_pressure',
    label: 'OT HVAC positive pressure & temperature logged',
    type: 'YES_NO',
    required: true,
  },
]

// ---------------------------------------------------------------------------
// 10. OT_BETWEEN_CASE_CLEANING
// ---------------------------------------------------------------------------
export const HOS_OT_BETWEEN_CASE_CLEANING_FIELDS: OpsTemplateField[] = [
  {
    key: 'table_accessories_disinfected',
    label: 'OT table, straps, and accessories disinfected',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'floor_cleaned_spills_treated',
    label: 'Floor mopped with hospital disinfectant',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'anesthesia_equipment_cleaned',
    label: 'Anesthesia equipment & circuit replaced',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'waste_linen_cleared',
    label: 'Bio-medical waste and soiled linen bags removed',
    type: 'YES_NO',
    required: true,
  },
]

// ---------------------------------------------------------------------------
// 11. HOUSEKEEPING_ZONE_ROUND (Parity with hospital-20-50-beds kit)
// ---------------------------------------------------------------------------
export const HOS_HOUSEKEEPING_ZONE_ROUND_FIELDS: OpsTemplateField[] = [
  {
    key: 'scan_zone_qr',
    label: 'Scan the zone QR',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'floors_mopped',
    label: 'Floors mopped',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'washrooms_cleaned_and_stocked',
    label: 'Washrooms cleaned and stocked',
    type: 'PHOTO',
    required: true,
  },
  {
    key: 'bins_emptied_liners_changed',
    label: 'Bins emptied and liners changed',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'high_touch_surfaces_wiped',
    label: 'High-touch surfaces wiped',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'issues_found_raised_as_request',
    label: 'Issues found (leak, broken fitting), raised as a request',
    type: 'ACKNOWLEDGE',
    required: false,
  },
]

// ---------------------------------------------------------------------------
// 12. DISCHARGE_CLEAN_INSPECTION (Parity with hospital-20-50-beds kit)
// ---------------------------------------------------------------------------
export const HOS_DISCHARGE_CLEAN_INSPECTION_FIELDS: OpsTemplateField[] = [
  {
    key: 'linen_removed',
    label: 'Linen removed',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'bed_rails_mattress_cleaned',
    label: 'Bed, rails and mattress cleaned',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'locker_table_call_bell_cleaned',
    label: 'Locker, table and call bell cleaned',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'floor_cleaned',
    label: 'Floor cleaned',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'bed_made_fresh_linen',
    label: 'Bed made with fresh linen',
    type: 'PHOTO',
    required: true,
  },
  {
    key: 'inspected_by_not_cleaner',
    label: 'Inspected by (not the cleaner)',
    type: 'ACKNOWLEDGE',
    required: true,
  },
  {
    key: 'bed_released_ready',
    label: 'Bed released as ready',
    type: 'YES_NO',
    required: true,
  },
]

// ---------------------------------------------------------------------------
// 13. SPILL_RESPONSE
// ---------------------------------------------------------------------------
export const HOS_SPILL_RESPONSE_FIELDS: OpsTemplateField[] = [
  {
    key: 'spill_type',
    label: 'Spill type',
    type: 'CHOICE',
    options: ['BLOOD_BODY_FLUID', 'CHEMICAL', 'MERCURY', 'CYTOTOXIC', 'OTHER'],
    required: true,
  },
  {
    key: 'spill_kit_used',
    label: 'Appropriate spill kit deployed & PPE worn',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'disinfectant_applied',
    label: 'Disinfectant applied for recommended contact time',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'waste_bagged_appropriate_color',
    label: 'Absorbent and waste bagged in designated color bag',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'location_details',
    label: 'Location and place of incident',
    type: 'ACKNOWLEDGE',
    required: false,
  },
]

// ---------------------------------------------------------------------------
// 14. PHARMACY_FRIDGE_TEMPERATURE (Operations only: temp & action, no drugs/patients)
// ---------------------------------------------------------------------------
export const HOS_PHARMACY_FRIDGE_TEMPERATURE_FIELDS: OpsTemplateField[] = [
  {
    key: 'fridge_temperature',
    label: 'Recorded temperature (°C)',
    type: 'NUMBER',
    unit: '°C',
    required: true,
  },
  {
    key: 'in_target_range',
    label: 'Temperature within 2°C to 8°C target range',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'corrective_action_taken',
    label: 'Action taken if out of range',
    type: 'ACKNOWLEDGE',
    required: false,
  },
]

// ---------------------------------------------------------------------------
// 15. BMW_SEGREGATION_SPOT_AUDIT
// ---------------------------------------------------------------------------
export const HOS_BMW_SEGREGATION_SPOT_AUDIT_FIELDS: OpsTemplateField[] = [
  {
    key: 'yellow_bin_compliance',
    label: 'Yellow bin (anatomical, soiled, gauze only; no plastics/sharps)',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'red_bin_compliance',
    label: 'Red bin (contaminated recyclable plastics, tubing, catheters only)',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'white_translucent_container',
    label: 'White translucent puncture-proof box (sharps, needles, blades)',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'blue_box_compliance',
    label: 'Blue box/cardboard (glassware, ampoules, metallic implants)',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'barcoded_bags_labelled',
    label: 'Barcoded bags affixed and labelled per guidelines',
    type: 'YES_NO',
    required: true,
  },
]

// ---------------------------------------------------------------------------
// 16. FIRE_EXTINGUISHER_MONTHLY (Per-extinguisher check)
// ---------------------------------------------------------------------------
export const HOS_FIRE_EXTINGUISHER_MONTHLY_FIELDS: OpsTemplateField[] = [
  {
    key: 'extinguisher_location',
    label: 'Extinguisher location and identification tag',
    type: 'ACKNOWLEDGE',
    required: true,
  },
  {
    key: 'extinguisher_type',
    label: 'Type (CO2, Water, Foam, Clean Agent, ABC Powder)',
    type: 'CHOICE',
    options: ['ABC_POWDER', 'CO2', 'WATER', 'FOAM', 'CLEAN_AGENT'],
    required: true,
  },
  {
    key: 'pressure_gauge_ok',
    label: 'Pressure gauge in green zone',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'seal_and_pin_intact',
    label: 'Tamper pin and lead seal intact',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'tag_dated',
    label: 'Inspection tag signed and updated',
    type: 'YES_NO',
    required: true,
  },
]

// ---------------------------------------------------------------------------
// 17. AMBULANCE_READINESS
// ---------------------------------------------------------------------------
export const HOS_AMBULANCE_READINESS_FIELDS: OpsTemplateField[] = [
  {
    key: 'vehicle_fuel_and_battery',
    label: 'Vehicle fuel adequate, battery, siren, and beacons operational',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'onboard_oxygen_and_suction',
    label: 'Onboard portable oxygen cylinder full and suction unit verified',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'transport_ventilator_defib',
    label: 'Transport ventilator, monitor, and automated defibrillator checked',
    type: 'YES_NO',
    required: true,
  },
  {
    key: 'emergency_kit_stock_in_date',
    label: 'Emergency medical kit stocked and items in-date',
    type: 'YES_NO',
    required: true,
  },
]

// ---------------------------------------------------------------------------
// Starter Template Definitions Map
// ---------------------------------------------------------------------------
export const HOSPITAL_STARTER_TEMPLATES: Record<string, HosStarterTemplateDef> = {
  fire_drill: {
    starterKey: 'FIRE_DRILL',
    slug: 'fire_drill',
    name: 'Fire Evacuation Drill Record',
    kind: 'LOG',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 1440,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_FIRE_DRILL_FIELDS,
  },
  fire_equipment_monthly: {
    starterKey: 'FIRE_EQUIPMENT_MONTHLY',
    slug: 'fire_equipment_monthly',
    name: 'Monthly Fire Safety Equipment Inspection',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 2880,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_FIRE_EQUIPMENT_MONTHLY_FIELDS,
  },
  electrical_inspection: {
    starterKey: 'ELECTRICAL_INSPECTION',
    slug: 'electrical_inspection',
    name: 'Electrical Installation & Backup Inspection',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 2880,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_ELECTRICAL_INSPECTION_FIELDS,
  },
  kitchen_log: {
    starterKey: 'KITCHEN_LOG',
    slug: 'kitchen_log',
    name: 'Hospital Kitchen Daily Hygiene & Food Safety Log',
    kind: 'LOG',
    safetyTagged: false,
    patientLinked: false,
    lateAfterMinutes: 720,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_KITCHEN_LOG_FIELDS,
  },
  laundry_log: {
    starterKey: 'LAUNDRY_LOG',
    slug: 'laundry_log',
    name: 'Hospital Laundry Linen Dispatch & Receipt Log',
    kind: 'LOG',
    safetyTagged: false,
    patientLinked: false,
    lateAfterMinutes: 720,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_LAUNDRY_LOG_FIELDS,
  },
  crash_cart_check: {
    starterKey: 'CRASH_CART_CHECK',
    slug: 'crash_cart_check',
    name: 'Crash-Cart Shift & Seal Check',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 480,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_CRASH_CART_CHECK_FIELDS,
  },
  oxygen_suction_readiness: {
    starterKey: 'OXYGEN_SUCTION_READINESS',
    slug: 'oxygen_suction_readiness',
    name: 'Oxygen & Suction System Daily Readiness Check',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 720,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_OXYGEN_SUCTION_READINESS_FIELDS,
  },
  icu_equipment_readiness: {
    starterKey: 'ICU_EQUIPMENT_READINESS',
    slug: 'icu_equipment_readiness',
    name: 'ICU Critical Care Equipment Readiness Check',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 720,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_ICU_EQUIPMENT_READINESS_FIELDS,
  },
  ot_room_readiness: {
    starterKey: 'OT_ROOM_READINESS',
    slug: 'ot_room_readiness',
    name: 'Operation Theatre Morning Readiness Check',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 720,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_OT_ROOM_READINESS_FIELDS,
  },
  ot_between_case_cleaning: {
    starterKey: 'OT_BETWEEN_CASE_CLEANING',
    slug: 'ot_between_case_cleaning',
    name: 'Operation Theatre Between-Case Turnover Cleaning',
    kind: 'CHECK',
    safetyTagged: false,
    patientLinked: false,
    lateAfterMinutes: 120,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_OT_BETWEEN_CASE_CLEANING_FIELDS,
  },
  housekeeping_zone_round: {
    starterKey: 'HOUSEKEEPING_ZONE_ROUND',
    slug: 'housekeeping_zone_round',
    name: 'Housekeeping Zone Cleaning Round',
    kind: 'CHECK',
    safetyTagged: false,
    patientLinked: false,
    lateAfterMinutes: 720,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_HOUSEKEEPING_ZONE_ROUND_FIELDS,
  },
  discharge_clean_inspection: {
    starterKey: 'DISCHARGE_CLEAN_INSPECTION',
    slug: 'discharge_clean_inspection',
    name: 'Discharge Bed Clean & Release Inspection',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 180,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_DISCHARGE_CLEAN_INSPECTION_FIELDS,
  },
  spill_response: {
    starterKey: 'SPILL_RESPONSE',
    slug: 'spill_response',
    name: 'Hospital Hazardous Spill Response Log',
    kind: 'LOG',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 240,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_SPILL_RESPONSE_FIELDS,
  },
  pharmacy_fridge_temperature: {
    starterKey: 'PHARMACY_FRIDGE_TEMPERATURE',
    slug: 'pharmacy_fridge_temperature',
    name: 'Hospital Pharmacy Cold-Chain Fridge Temperature Log',
    kind: 'LOG',
    safetyTagged: false,
    patientLinked: false,
    lateAfterMinutes: 720,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_PHARMACY_FRIDGE_TEMPERATURE_FIELDS,
  },
  bmw_segregation_spot_audit: {
    starterKey: 'BMW_SEGREGATION_SPOT_AUDIT',
    slug: 'bmw_segregation_spot_audit',
    name: 'Bio-Medical Waste Segregation Spot Audit',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 1440,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_BMW_SEGREGATION_SPOT_AUDIT_FIELDS,
  },
  fire_extinguisher_monthly: {
    starterKey: 'FIRE_EXTINGUISHER_MONTHLY',
    slug: 'fire_extinguisher_monthly',
    name: 'Per-Extinguisher Monthly Physical & Pressure Inspection',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 2880,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_FIRE_EXTINGUISHER_MONTHLY_FIELDS,
  },
  ambulance_readiness: {
    starterKey: 'AMBULANCE_READINESS',
    slug: 'ambulance_readiness',
    name: 'Emergency Ambulance Daily Readiness Inspection',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 720,
    source: HOS_STARTER_SOURCE,
    edition: HOS_STARTER_EDITION,
    jurisdiction: HOS_STARTER_JURISDICTION,
    starter: true,
    fields: HOS_AMBULANCE_READINESS_FIELDS,
  },
}

export type HosStarterTemplateSlug = keyof typeof HOSPITAL_STARTER_TEMPLATES
export const HOS_STARTER_SLUGS = Object.keys(HOSPITAL_STARTER_TEMPLATES) as HosStarterTemplateSlug[]

// ---------------------------------------------------------------------------
// Hospital Compliance Starter Definitions (C5.a5)
// ---------------------------------------------------------------------------
export interface HospitalComplianceStarterDef {
  key: string
  title: string
  category: OpsComplianceCategory
  authority: string
  suggestedRenewMonths: number
  notes: string
  imagingOnly?: boolean
}

export const HOSPITAL_COMPLIANCE_STARTERS: Record<string, HospitalComplianceStarterDef> = {
  FIRE_NOC: {
    key: 'FIRE_NOC',
    title: 'Fire Safety NOC',
    category: 'CERTIFICATE',
    authority: 'State Fire Service / Municipal Fire Authority',
    suggestedRenewMonths: 12,
    notes: 'Starter suggestion — check renewal period and conditions with your legal advisor.',
  },
  BMW_AUTHORISATION: {
    key: 'BMW_AUTHORISATION',
    title: 'Bio-Medical Waste Authorisation',
    category: 'LICENCE',
    authority: 'State Pollution Control Board (SPCB / PCC)',
    suggestedRenewMonths: 36,
    notes: 'Starter suggestion — check renewal period and conditions with your legal advisor.',
  },
  CONSENT_TO_OPERATE: {
    key: 'CONSENT_TO_OPERATE',
    title: 'Consent to Operate (Water & Air Acts)',
    category: 'LICENCE',
    authority: 'State Pollution Control Board (SPCB / PCC)',
    suggestedRenewMonths: 60,
    notes: 'Starter suggestion — check renewal period and conditions with your legal advisor.',
  },
  LIFT_LICENCE: {
    key: 'LIFT_LICENCE',
    title: 'Lift / Elevator Operational Licence',
    category: 'LICENCE',
    authority: 'State Electrical Inspectorate / Lift Authority',
    suggestedRenewMonths: 12,
    notes: 'Starter suggestion — check renewal period and conditions with your legal advisor.',
  },
  ELECTRICAL_INSPECTION: {
    key: 'ELECTRICAL_INSPECTION',
    title: 'Periodic Electrical Installation Inspection Certificate',
    category: 'INSPECTION',
    authority: 'Chief Electrical Inspector to Government (CEIG)',
    suggestedRenewMonths: 12,
    notes: 'Starter suggestion — check renewal period and conditions with your legal advisor.',
  },
  DG_SET_APPROVAL: {
    key: 'DG_SET_APPROVAL',
    title: 'Diesel Generator Set Installation Approval',
    category: 'CERTIFICATE',
    authority: 'State Electrical Inspectorate & Pollution Control Board',
    suggestedRenewMonths: 24,
    notes: 'Starter suggestion — check renewal period and conditions with your legal advisor.',
  },
  CLINICAL_ESTABLISHMENT_REGISTRATION: {
    key: 'CLINICAL_ESTABLISHMENT_REGISTRATION',
    title: 'Clinical Establishment Registration / Licence',
    category: 'REGISTRATION',
    authority: 'District Registering Authority / State Health Department',
    suggestedRenewMonths: 60,
    notes: 'Starter suggestion — check renewal period and conditions with your legal advisor.',
  },
  PHARMACY_LICENCE: {
    key: 'PHARMACY_LICENCE',
    title: 'Hospital Pharmacy Drug Licence (Form 20/21)',
    category: 'LICENCE',
    authority: 'State Drugs Control Administration',
    suggestedRenewMonths: 60,
    notes: 'Starter suggestion — check renewal period and conditions with your legal advisor.',
  },
  AERB_LICENCE: {
    key: 'AERB_LICENCE',
    title: 'AERB Diagnostic Radiology Equipment Licence',
    category: 'LICENCE',
    authority: 'Atomic Energy Regulatory Board (eLORA portal)',
    suggestedRenewMonths: 60,
    notes: 'Applicable only if facility operates imaging (X-Ray, CT, C-Arm). Check with your advisor.',
    imagingOnly: true,
  },
}

export function isKnownHospitalStarterLicenceKey(key: string): boolean {
  if (typeof key !== 'string') return false
  return key.trim() in HOSPITAL_COMPLIANCE_STARTERS
}

